import CachedIcon from '@mui/icons-material/Cached'
import EditIcon from '@mui/icons-material/Edit'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import { FilterAltOff, Search } from '@mui/icons-material'
import GroupsIcon from '@mui/icons-material/Groups'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import {
    Autocomplete,
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Collapse,
    Dialog,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip
} from '@mui/material'
import { GridActionsCellItem } from '@mui/x-data-grid'
import { useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { ControlledAutocomplete } from 'src/components/forms/ControlledAutocomplete'
import { CountryAvatar } from 'src/components/icons/CountryAvatar'
import { useConfirmationDialog } from 'src/hooks/useConfirmationDialog'
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import {
    ItecColFilters,
    ItecDataGrid,
    ItecGridColsDef as GridColsDef,
    useItecDataGrid
} from 'src/lib/itaptec/datagrid/src'
import { EntityWithIdAndDescriptionFields, schemaSelectionOption } from 'src/types'
import { z } from 'zod'
import { WrapperTable } from '../components'
import { HttpUnidadNegocio } from '../unidades-negocio/api'
import { HttpSectorComercialABM } from './api'
import EditSectorComercial from './EditSectorComercial'
import EditSectorComercialForm from './EditSectorComercialForm'
import { SectorComercialViewDTO, SectorComercialViewDTOFields } from './types'

const schemaFiltro = z.object({
    nombre: z.string().optional(),
    codsPais: z.array(schemaSelectionOption).optional(),
    codsUnidadNegocio: z.array(schemaSelectionOption).optional()
})

type SchemaFiltro = z.infer<typeof schemaFiltro>

function TableSectoresComerciales() {
    const { askConfirmation } = useConfirmationDialog()
    const { paisLogos } = usePaisLogo()
    const [filtersOpen, setFiltersOpen] = useState(true)
    const [selectedSectorComercial, setSelectedSectorComercial] = useState<SectorComercialViewDTO>()
    const [selectedSectorEdit, setSelectedSectorEdit] = useState<SectorComercialViewDTO>()
    const [allUnidades, setAllUnidades] = useState<{ id: number; descripcion: string; codPais: number | null }[]>([])

    useEffect(() => {
        HttpUnidadNegocio.getAll().then(res =>
            setAllUnidades(res.map(s => ({ id: s.id, descripcion: s.nombreUnidadNegocio, codPais: s.codPais })))
        )
    }, [])

    const getSectorDisplayName = (sector: SectorComercialViewDTO) =>
        sector[SectorComercialViewDTOFields.Nombre] || 'este sector comercial'

    const columnas: GridColsDef<SectorComercialViewDTO>[] = [
        {
            headerName: 'Sector Comercial',
            field: SectorComercialViewDTOFields.Nombre,
            renderCell: ({ row }) => (
                <Stack direction='row' alignItems='center' spacing={1.5} sx={{ minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            display: 'grid',
                            placeItems: 'center',
                            fontWeight: 700,
                            fontSize: 13
                        }}
                    >
                        {(row[SectorComercialViewDTOFields.Nombre]?.[0] ?? '?').toUpperCase()}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.25 }}>
                            {row[SectorComercialViewDTOFields.Nombre] || 'Sin nombre'}
                        </Box>
                        <Box
                            sx={{
                                fontSize: 12,
                                color: 'text.secondary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                        >
                            {row[SectorComercialViewDTOFields.NombreUnidadNegocio] || 'Sin unidad de negocio'}
                        </Box>
                    </Box>
                </Stack>
            ),
            flex: 3
        },
        {
            headerName: 'Unidad de Negocio',
            field: SectorComercialViewDTOFields.NombreUnidadNegocio,
            flex: 2
        },
        {
            headerName: 'Pais',
            field: SectorComercialViewDTOFields.NombrePais,
            flex: 1,
            renderCell: ({ row: sector }) => (
                <Chip
                    label={sector[SectorComercialViewDTOFields.NombrePais]}
                    size='small'
                    variant='outlined'
                    avatar={<CountryAvatar countryCode={sector[SectorComercialViewDTOFields.CodPais]} />}
                />
            )
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Acciones',
            align: 'center',
            headerAlign: 'center',
            minWidth: 132,
            getActions: ({ row }) => [
                <GridActionsCellItem
                    key={1}
                    onClick={() => {
                        setSelectedSectorEdit(row)
                    }}
                    icon={
                        <Tooltip title='Editar'>
                            <EditIcon color='info' />
                        </Tooltip>
                    }
                    label='Editar'
                    showInMenu={false}
                />,
                <GridActionsCellItem
                    key={2}
                    onClick={() => {
                        setSelectedSectorComercial(row)
                    }}
                    icon={
                        <Tooltip title='Grupos'>
                            <GroupsIcon color='primary' />
                        </Tooltip>
                    }
                    label='Grupos'
                    showInMenu={false}
                />,
                <GridActionsCellItem
                    key={3}
                    onClick={() =>
                        askConfirmation(() => {
                            const promise = HttpSectorComercialABM.deactivate(row.id)
                            toast
                                .promise(promise, {
                                    success: 'Inactivado',
                                    error: 'No se pudo inactivar',
                                    loading: 'Inactivando'
                                })
                                .then(() => refetch())
                        }, `Esta seguro que desea inactivar ${getSectorDisplayName(row)}?`)
                    }
                    icon={
                        <Tooltip title='Inactivar'>
                            <PersonRemoveIcon color='error' />
                        </Tooltip>
                    }
                    label='Inactivar'
                    showInMenu={false}
                />
            ]
        }
    ]

    const filtros: ItecColFilters<SchemaFiltro> = {
        [SectorComercialViewDTOFields.Nombre]: {
            type: 'text',
            name: 'nombre',
            props: { debounceTime: 500, size: 'small' }
        },
        [SectorComercialViewDTOFields.NombrePais]: {
            type: 'multiselect',
            name: 'codsPais',
            props: { getOptions: () => Promise.resolve([]), size: 'small' }
        },
        [SectorComercialViewDTOFields.NombreUnidadNegocio]: {
            type: 'multiselect',
            name: 'codsUnidadNegocio',
            props: { getOptions: () => Promise.resolve([]), size: 'small' }
        }
    }

    const {
        datagridProps,
        control: {
            query: { refetch },
            form: filterForm
        }
    } = useItecDataGrid<SectorComercialViewDTO, SchemaFiltro>({
        pagination: false,
        columns: columnas,
        filters: filtros,
        getEntities: filter =>
            HttpSectorComercialABM.getSectoresComerciales().then(data => {
                if (!filter) return data

                let filtered = data

                const nombre = filter.nombre
                if (nombre) {
                    const lower = nombre.toLowerCase()
                    filtered = filtered.filter(s =>
                        s[SectorComercialViewDTOFields.Nombre]?.toLowerCase().includes(lower)
                    )
                }

                const codsPais = filter.codsPais
                if (codsPais && codsPais.length > 0) {
                    const paisValues = codsPais.map(p => (p as any).id ?? p.value)
                    filtered = filtered.filter(s => paisValues.includes(s[SectorComercialViewDTOFields.CodPais]))
                }

                const codsUnidadNegocio = filter.codsUnidadNegocio
                if (codsUnidadNegocio && codsUnidadNegocio.length > 0) {
                    const unValues = codsUnidadNegocio.map(u => (u as any).id ?? u.value)
                    filtered = filtered.filter(
                        s =>
                            s[SectorComercialViewDTOFields.CodUnidadNegocio] !== null &&
                            unValues.includes(s[SectorComercialViewDTOFields.CodUnidadNegocio]!)
                    )
                }

                return filtered
            }),
        density: 'standard'
    })

    const cleanDatagridProps = {
        ...datagridProps,
        columns: datagridProps.columns.map(col => ({
            ...col,
            disableColumnMenu: true,
            renderHeader: undefined
        })),
        slots: undefined
    }

    const handleClearFilters = () => {
        if (!filterForm) return
        filterForm.reset({
            nombre: undefined,
            codsPais: undefined,
            codsUnidadNegocio: undefined
        })
    }

    return (
        <WrapperTable>
            <Card
                elevation={0}
                sx={{
                    border: theme => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    overflow: 'hidden'
                }}
            >
                <CardHeader
                    sx={{
                        px: 3,
                        py: 2.5,
                        '& .MuiCardHeader-title': { fontWeight: 700 },
                        '& .MuiCardHeader-subheader': { mt: 0.5 }
                    }}
                    title='Administracion de sectores comerciales'
                    subheader='Gestiona sectores, unidades de negocio, paises y grupos asociados'
                    action={
                        <Stack direction='row' alignItems='center' spacing={1}>
                            <Tooltip title={filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'}>
                                <IconButton onClick={() => setFiltersOpen(prev => !prev)} size='small'>
                                    <FilterAltIcon color={filtersOpen ? 'primary' : 'inherit'} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title='Actualizar'>
                                <span>
                                    <IconButton disabled={datagridProps.loading} onClick={() => refetch()} size='small'>
                                        <CachedIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    }
                />

                <Collapse in={filtersOpen}>
                    {filterForm && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 2,
                                alignItems: 'center',
                                mx: 3,
                                mb: 2,
                                p: 2,
                                borderRadius: 1,
                                border: theme => `1px solid ${theme.palette.divider}`,
                                bgcolor: 'background.default'
                            }}
                        >
                            <Controller
                                control={filterForm.control}
                                name='nombre'
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        value={field.value ?? ''}
                                        label='Nombre'
                                        size='small'
                                        sx={{ minWidth: 210, flex: 1 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position='start'>
                                                    <Search fontSize='small' />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                )}
                            />
                            <Box sx={{ minWidth: 210, flex: 1 }}>
                                <ControlledAutocomplete
                                    control={filterForm.control}
                                    name='codsPais'
                                    label='Pais'
                                    loadOptions={() =>
                                        Promise.resolve(paisLogos).then(res =>
                                            res.map(s => ({
                                                id: s.id,
                                                descripcion: s[EntityWithIdAndDescriptionFields.Description]
                                            }))
                                        )
                                    }
                                    multiple
                                />
                            </Box>
                            <Box sx={{ minWidth: 230, flex: 1 }}>
                                <Controller
                                    control={filterForm.control}
                                    name='codsUnidadNegocio'
                                    render={({ field }) => {
                                        const selectedPaises = filterForm.watch('codsPais')
                                        const paisIds = (selectedPaises ?? []).map((p: any) => p.id ?? p.value)
                                        const unidadesFiltradas =
                                            paisIds.length > 0
                                                ? allUnidades.filter(
                                                      u => u.codPais !== null && paisIds.includes(u.codPais)
                                                  )
                                                : []
                                        const disabled = paisIds.length === 0

                                        return (
                                            <Autocomplete
                                                multiple
                                                disabled={disabled}
                                                options={unidadesFiltradas}
                                                value={((field.value ?? []) as any[]).filter((v: any) =>
                                                    unidadesFiltradas.some(u => u.id === (v.id ?? v.value))
                                                )}
                                                onChange={(_, data) => field.onChange(data)}
                                                getOptionLabel={(option: any) => option.descripcion ?? ''}
                                                isOptionEqualToValue={(option: any, value: any) =>
                                                    option.id === (value.id ?? value.value)
                                                }
                                                size='small'
                                                renderInput={params => (
                                                    <TextField
                                                        {...params}
                                                        label='Unidad de Negocio'
                                                        variant='outlined'
                                                    />
                                                )}
                                                ChipProps={{ color: 'primary', size: 'small' }}
                                            />
                                        )
                                    }}
                                />
                            </Box>
                            <Tooltip title='Limpiar filtros'>
                                <IconButton size='small' onClick={handleClearFilters}>
                                    <FilterAltOff fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Collapse>

                <CardContent sx={{ pt: 0, px: 3, pb: 3 }}>
                    <Box
                        sx={{
                            '& .MuiDataGrid-root': {
                                border: theme => `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                                overflow: 'hidden'
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                bgcolor: 'background.default'
                            },
                            '& .MuiDataGrid-row:hover': {
                                bgcolor: 'action.hover'
                            }
                        }}
                    >
                        <ItecDataGrid {...cleanDatagridProps} />
                    </Box>
                </CardContent>
            </Card>
            <>
                <Dialog
                    open={!!selectedSectorComercial}
                    fullWidth
                    maxWidth='lg'
                    keepMounted={false}
                    sx={{ maxHeight: '100%' }}
                >
                    {selectedSectorComercial && (
                        <EditSectorComercial
                            onClose={() => {
                                setSelectedSectorComercial(undefined)
                            }}
                            sector={selectedSectorComercial}
                        />
                    )}
                </Dialog>
                <Dialog open={!!selectedSectorEdit} fullWidth maxWidth='sm' keepMounted={false}>
                    {selectedSectorEdit && (
                        <EditSectorComercialForm
                            onClose={() => {
                                setSelectedSectorEdit(undefined)
                                refetch()
                            }}
                            sector={selectedSectorEdit}
                        />
                    )}
                </Dialog>
            </>
        </WrapperTable>
    )
}

export default TableSectoresComerciales
